"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.findBestContributorMatch = exports.buildContributorMatchIndex = exports.DEFAULT_FUZZY_MATCH_THRESHOLD = void 0;
const jaro_winkler_1 = require("./jaro-winkler");
const name_parser_1 = require("./name-parser");
/** Fallback threshold when no per-company override is configured. */
exports.DEFAULT_FUZZY_MATCH_THRESHOLD = 0.9;
const pushIntoMultimap = (map, key, value) => {
    const existing = map.get(key);
    if (existing) {
        existing.push(value);
    }
    else {
        map.set(key, [value]);
    }
};
/**
 * Build an index over a list of candidate items keyed by normalized full name and last name.
 *
 * Cost is O(n) and the resulting index is intended to be reused across many lookups
 * (e.g. one per payroll row) so each lookup is bounded by the size of the last-name
 * cohort rather than the full candidate set.
 */
const buildContributorMatchIndex = (items, getName) => {
    const byExactName = new Map();
    const byLastName = new Map();
    for (const item of items) {
        const raw = getName(item);
        const parsed = (0, name_parser_1.parsePersonName)(raw);
        if (!parsed) {
            continue;
        }
        pushIntoMultimap(byExactName, parsed.full, item);
        pushIntoMultimap(byLastName, parsed.last, { item, full: parsed.full, first: parsed.first });
    }
    return { byExactName, byLastName };
};
exports.buildContributorMatchIndex = buildContributorMatchIndex;
/**
 * Find the best contributor for a query name using:
 * 1. Exact full-name fast path. When multiple DB rows share the same canonical name they
 *    are treated as duplicates of the same person — first one is picked deterministically.
 * 2. Jaro-Winkler scoring against the candidates that share the query's last name. JW is
 *    prefix-weighted, which matches human intuition about typos: "Jhon"/"John" scores
 *    high, "DoriAnn"/"Brianna" does not.
 *
 * Cohort entries are first grouped by normalized full name so duplicates compete as a
 * single candidate. Ties **between distinct names** at the top score are rejected (returns
 * null) to avoid silently picking between two genuinely different people.
 */
const findBestContributorMatch = (index, rawName, threshold) => {
    const parsed = (0, name_parser_1.parsePersonName)(rawName);
    if (!parsed) {
        return null;
    }
    const exactMatches = index.byExactName.get(parsed.full);
    if (exactMatches !== undefined && exactMatches.length > 0) {
        return { item: exactMatches[0], score: 1, winners: exactMatches };
    }
    const cohort = index.byLastName.get(parsed.last);
    if (!cohort || cohort.length === 0) {
        return null;
    }
    // No first-name signal on the query — only the exact-match path could have helped here,
    // and it didn't. Bail rather than score against an empty string and inflate every
    // candidate to the same low Jaro value.
    if (parsed.first.length === 0) {
        return null;
    }
    // Group cohort entries by normalized full name so N rows with identical canonical
    // names (e.g. five "Jennifer Jones" in the table) compete as one candidate. We
    // keep the first-name string alongside, since within a cohort the last name is
    // identical for everyone and only the first/middle portion carries match signal.
    const itemsByFull = new Map();
    for (const entry of cohort) {
        const existing = itemsByFull.get(entry.full);
        if (existing) {
            existing.items.push(entry.item);
        }
        else {
            itemsByFull.set(entry.full, { items: [entry.item], first: entry.first });
        }
    }
    let bestScore = -1;
    let bestFull = null;
    let tied = false;
    for (const [full, { first }] of itemsByFull) {
        if (first.length === 0) {
            // Candidate has no first name (single-token canonical name). The exact path
            // would have caught a real match; skip in the fuzzy scan to avoid scoring "".
            continue;
        }
        // Score the first-and-middle portion only. The last name is guaranteed identical
        // within this cohort, so including it double-counts and lets common-surname
        // pairs like "Mona Ballard" vs "Mary Ballard" sneak above the threshold.
        const score = (0, jaro_winkler_1.jaroWinklerSimilarity)(parsed.first, first);
        if (score < threshold) {
            continue;
        }
        if (score > bestScore) {
            bestScore = score;
            bestFull = full;
            tied = false;
        }
        else if (score === bestScore) {
            tied = true;
        }
    }
    if (bestFull === null || tied) {
        return null;
    }
    const winners = itemsByFull.get(bestFull).items;
    return { item: winners[0], score: bestScore, winners };
};
exports.findBestContributorMatch = findBestContributorMatch;
